import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import Toast from '@/components/base/Toast';
import { useToast } from '@/hooks/useToast';
import { movies } from '@/mocks/movies';
import { actors } from '@/mocks/actors';

export default function MovieDetail() {
  const { id } = useParams();
  const movie = useMemo(() => movies.find((m) => m.id === id), [id]);
  const { message, show } = useToast();
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setFavorited(false);
  }, [id]);

  const actorNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    actors.forEach((a) => {
      map[a.id] = a.name;
    });
    return map;
  }, []);

  const cast = useMemo(
    () => (movie ? actors.filter((a) => movie.actorIds.includes(a.id)) : []),
    [movie],
  );

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
      .slice(0, 4);
  }, [movie]);

  const handleFavorite = () => {
    setFavorited((v) => !v);
    show(favorited ? '已取消收藏（功能演示中）' : '已加入收藏（功能演示中，未保存数据）');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      show('链接已复制到剪贴板（功能演示中）');
    } catch {
      show('复制失败，请手动复制地址栏链接');
    }
  };

  if (!movie) {
    return (
      <SiteLayout>
        <title>未找到该影片资料 - 蜜桃成熟时33D</title>
        <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-film-line text-4xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            未找到该影片资料
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-500">
            影片可能已被移除或链接有误，你可以返回分类页浏览其他作品。
          </p>
          <Link
            to="/movies"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回电影分类
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <title>{`${movie.title}剧情介绍与演员信息 - 蜜桃成熟时33D`}</title>
      <meta
        name="description"
        content={`${movie.title}的详细剧情介绍、影片信息与主要演员资料，包含上映年份、类型、时长等元信息，以及相关影片推荐。`}
      />
      <meta name="keywords" content={`${movie.title},${movie.title}剧情,${movie.title}演员,${movie.genres.join(',')}`} />
      <Toast message={message} />

      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={movie.backdrop}
            alt={`${movie.title}背景`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/80 to-foreground-950/60"></div>
        <div className="relative mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '电影分类', to: '/movies' },
              { label: movie.title },
            ]}
          />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="w-full lg:col-span-4">
              <div className="relative mx-auto aspect-[2/3] w-full max-w-xs overflow-hidden rounded-lg border border-background-50/20">
                <img
                  src={movie.poster}
                  alt={`${movie.title}海报`}
                  title={`${movie.title}电影海报`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>
            <div className="w-full lg:col-span-8">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-medium text-primary-200">
                <i className="ri-movie-2-line"></i>
                {movie.tags[0] || '影片资料'}
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200">
                {movie.excerpt}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-background-200">
                <span className="flex items-center gap-1 rounded-md bg-background-50/10 px-2.5 py-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  演示评分 {movie.rating.toFixed(1)}
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1">{movie.year}</span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                  {movie.region}
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                  {movie.duration} 分钟
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                  导演 {movie.director}
                </span>
                <span className="rounded-md bg-background-50/10 px-2.5 py-1">
                  {movie.language}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                    favorited
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-50/30 bg-background-50/10 text-background-50 hover:bg-background-50/20'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/20 cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/movies?genre=${encodeURIComponent(g)}`}
                    className="whitespace-nowrap rounded-full border border-background-50/25 px-3 py-1 text-xs text-background-200 transition-colors hover:border-primary-300 hover:text-primary-200"
                  >
                    {g}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-12 md:px-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          {/* Player demo */}
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <div className="relative w-full overflow-hidden rounded-md bg-foreground-950">
              <div className="aspect-video w-full">
                <img
                  src={movie.backdrop}
                  alt={`${movie.title}播放演示区域`}
                  className="h-full w-full object-top opacity-70"
                />
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-play-fill text-3xl"></i>
                </span>
                <span className="rounded-full bg-foreground-950/70 px-3 py-1 text-xs text-background-100">
                  播放区域为演示画面，本站不提供真实影片播放
                </span>
              </div>
            </div>
            <p className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-information-line text-accent-600"></i>
              提示：以上为播放界面演示效果，无真实播放接口，仅用于展示页面布局。
            </p>
          </div>

          {/* Synopsis */}
          <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-book-open-line"></i>
              </span>
              <h2 className="font-heading text-xl font-bold text-foreground-950">详细剧情介绍</h2>
            </div>
            <div className="mt-4 space-y-4">
              {movie.synopsis.map((p, idx) => (
                <p key={idx} className="text-sm leading-7 text-foreground-700">
                  {p}
                </p>
              ))}
            </div>
          </div>

          {/* Cast */}
          <div className="mt-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">主要演员</h2>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {cast.map((a) => (
                <Link
                  key={a.id}
                  to={`/actors/detail/${a.id}`}
                  className="group flex flex-col items-center rounded-lg border border-background-200 bg-background-50 p-4 text-center transition-colors hover:border-primary-300"
                >
                  <div className="h-20 w-20 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={a.avatar}
                      alt={`${a.name}照片`}
                      title={`${a.name}演员资料`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="mt-3 text-sm font-medium text-foreground-950">{a.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{a.roleTag}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Aside */}
        <aside className="flex flex-col gap-6 lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">影片信息</h2>
            <dl className="mt-4 space-y-3 text-sm">
              {[
                ['影片名称', movie.title],
                ['上映年份', String(movie.year)],
                ['影片类型', movie.genres.join(' / ')],
                ['制片地区', movie.region],
                ['影片时长', `${movie.duration} 分钟`],
                ['影片语言', movie.language],
                ['导演', movie.director],
                ['演示评分', movie.rating.toFixed(1)],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-4 border-b border-background-200 pb-3 last:border-0 last:pb-0">
                  <dt className="shrink-0 text-foreground-500">{k}</dt>
                  <dd className="text-right font-medium text-foreground-900">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs leading-relaxed text-foreground-400">
              以上信息均为虚构演示数据，不涉及任何真实影片资料。
            </p>
          </div>

          <div className="rounded-lg bg-secondary-100 p-6">
            <h2 className="font-heading text-lg font-bold text-secondary-950">所属分类</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <Link
                  key={g}
                  to={`/movies?genre=${encodeURIComponent(g)}`}
                  className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-background-100"
                >
                  {g}
                </Link>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-secondary-900/80">
              点击分类可查看同类型影片列表。
            </p>
          </div>
        </aside>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="border-t border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-12 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((m) => (
                <MovieCard
                  key={m.id}
                  movie={m}
                  actorNames={m.actorIds.slice(0, 2).map((aid) => actorNameMap[aid])}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}